import React, {useMemo, useRef, useState} from 'react';
import {
    IonList,
    IonItem,
    IonLabel,
    IonSearchbar,
    IonSpinner,
    IonNote,
    IonButton,
    IonModal,
    IonHeader, IonToolbar, IonButtons, IonTitle, IonContent
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import type {Item} from "./AppTypeaheadTypes";

interface AutocompleteProps {
    /**
     * An optional label to display above the search bar.
     */
    label?: string;

    /**
     * Called when a user picks a suggestion or enters a custom input.
     */
    onSelect: (value: Item) => void;

    /**
     * Minimum number of characters before we attempt to fetch server data.
     * Defaults to 2.
     */
    minChars?: number;

    /**
     * Debounce delay (milliseconds) for the search input.
     * Defaults to 300 ms.
     */
    debounceDelay?: number;

    /**
     * Placeholder for the search bar.
     */
    placeholder?: string;

    /**
     * Existing value when controlled from outside (optional).
     */
    value?: Item;

    /**
     * Whether the input is disabled.
     */
    disabled: boolean;

    /**
     * Autocomplete data url
     */
    fetchItems?: (query: string) => Promise<Item[]>;

    /**
     * Values the fetched items depend on, part of the query cache key
     */
    cacheKey?: unknown;

    /**
     * Text of the selected value on the button, the title by default
     */
    formatValue?: (value: Item) => string;
}

export const Autocomplete: React.FC<AutocompleteProps> = ({
                                                              label,
                                                              onSelect,
                                                              minChars = 1,
                                                              debounceDelay = 300,
                                                              placeholder = 'Type to search...',
                                                              value: externalValue,
                                                              disabled = false,
                                                              fetchItems = () => Promise.resolve([]),
                                                              cacheKey,
                                                              formatValue = (value) => value.title,
                                                          }) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const modal = useRef<any>();

    const triggerId = "open-autocomplete-modal-" + label!.replace(' ', '-');


    const {
        data: suggestions = [],
        isLoading,
        isError,
        refetch
    } = useQuery(
        {
            queryKey: ['autocomplete', triggerId, cacheKey],
            queryFn: () => fetchItems(searchTerm),
        },
    );

    /**
     * Items matching the search query (by title, description or id substring).
     * If no query is provided, all data will be rendered.
     */
    const filteredItems = useMemo(() => {
        if (!searchTerm) {
            return suggestions;
        }
        const normalizedQuery = searchTerm.toLowerCase();
        return suggestions.filter((item) => item.title.toLowerCase().includes(normalizedQuery)
            || item.description?.toLowerCase().includes(normalizedQuery)
            || item.id.toLowerCase().includes(normalizedQuery));
    }, [suggestions, searchTerm]);

    const handleSelect = (value: Item) => {
        // reopening the modal lists all items again instead of only the selected one
        setSearchTerm('');
        onSelect(value);

        modal.current?.dismiss();
    };

    const cancelChanges = () => {
        modal.current?.dismiss();
    };

    return (
        <div style={{ margin: '1rem' }}>
            <IonItem>
                <IonLabel>{ label }</IonLabel>
                <IonButton id={triggerId} disabled={disabled}
                           style={{textTransform: 'none', whiteSpace: 'normal', height: 'auto', maxWidth: '60%'}}>
                    { externalValue ? formatValue(externalValue) : 'Select an option ...' }
                </IonButton>
            </IonItem>

            <IonModal ref={modal} trigger={triggerId} onIonModalWillPresent={() => refetch()}>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton onClick={cancelChanges}>Cancel</IonButton>
                        </IonButtons>
                        <IonTitle>{label}</IonTitle>
                    </IonToolbar>
                    <IonToolbar>
                        <IonSearchbar
                            placeholder={placeholder}
                            value={searchTerm}
                            onIonInput={(e) => setSearchTerm(e.detail.value!)}
                            showClearButton="always"
                            debounce={300}
                        />
                    </IonToolbar>
                </IonHeader>

                <IonContent color="light" class="ion-padding">
                    <IonList>
                        {searchTerm.length >= minChars && (
                            <IonItem button onClick={() => handleSelect({id: searchTerm, title: searchTerm, description: ''})}>
                                <IonLabel>
                                    Use "<strong>{searchTerm}</strong>" (custom)
                                </IonLabel>
                                <IonNote slot="end">(No suggestions found)</IonNote>
                            </IonItem>
                        )}

                        {isLoading && (
                            <IonItem>
                                <IonSpinner slot="start" />
                                <IonLabel>Loading suggestions...</IonLabel>
                            </IonItem>
                        )}

                        {isError && (
                            <IonItem color="danger">
                                <IonLabel>Error loading suggestions</IonLabel>
                            </IonItem>
                        )}

                        {filteredItems.length > 0 && (
                            <>
                                {filteredItems.map(suggestion => (
                                    <IonItem key={suggestion.id} button onClick={() => handleSelect(suggestion)}>
                                        <IonLabel>
                                            <h3>{ suggestion.title }</h3>
                                            <p>{ suggestion.description } - { suggestion.id }</p>
                                        </IonLabel>
                                    </IonItem>
                                ))}
                            </>
                        )}
                    </IonList>

                    {/*<IonList id="modal-list" inset={true}>*/}
                    {/*    {filteredItems.map((item) => (*/}
                    {/*        <IonItem key={item.id} button onClick={() => confirmChanges(item)}>*/}
                    {/*            <IonLabel>*/}
                    {/*                <h3>{ item.title }</h3>*/}
                    {/*                <p>{ item.description } - { item.id }</p>*/}
                    {/*            </IonLabel>*/}
                    {/*        </IonItem>*/}
                    {/*    ))}*/}
                    {/*</IonList>*/}
                </IonContent>
            </IonModal>
        </div>
    );
};